import { useParams } from 'react-router-dom';
import { orderBy, uniqBy } from 'lodash';
import {
  GetRequiredMaterialMetricsQuery_,
  QuestionType_Enum_,
  useGetDrDatapointsTasksQuery,
  useGetMdrmDatapointsTasksQuery,
} from 'models';
import {
  AggregatedMetricsTableData,
  areChildrenMaterialOrCollected,
  FrameworkRefs,
  FrequencyEnums,
  hasChildOnBULevel,
  hasChildOnSubLevel,
  hasQuarterlyFrequency,
  isFullMetricOnBULevel,
  isFullParentMetricOnSubLevel,
  MetricsTableData,
  processNarrativeMetrics,
  TimePeriods,
  TimePeriodsEnums,
} from 'containers/Esrs/pieces';
import { DataCollectionLevel } from 'containers/Esrs/pieces/DataCollection';
import { GroupOrderOptions } from '../../AggregatedMetrics/AggregatedMetricsUtils';
import {
  AssessableMetrics,
  filterDuplicateRows,
  flattenMetricsNotAggregatable,
  flattenMetricsWithTags,
  getAllQuartersTaskReferences,
  getTaskReferences,
  metricsMissingRequiredTags,
  processBuMetrics,
  useGetBuMetrics,
  useGetMetrics,
} from './MetricsHelperFunctions';
import { useMemo } from 'react';
import { isDataCollectionMatching } from 'containers/Esrs/pieces/DataCollection/DataCollection.hooks';
import { TaskFilterOptions } from '../../MetricsTable/MetricsTable.utils';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';

export const useProcessMetrics = (
  withAssociation: boolean,
  groupOrOrder: GroupOrderOptions,
  taskFilter: TaskFilterOptions,
  selectedAdditionalTypes: string[],
  selectedUserId: string,
  selectedQuarter: TimePeriods,
  esrsAssessmentId: string,
  companyAssessmentId: string,
  parentAssessmentId: string,
  isGroup: boolean,
  noFilter: boolean,
  framework: string,
  selectedMaterialityOptions: string[]
) => {
  const { standardRef = '', disclosureRequirementRef, reportingUnitId } = useParams();
  const { user } = useAuth();
  const isBusinessUnit = !!reportingUnitId;

  const {
    aggregatedMetrics,
    companyAnswersData,
    qualitativeMetrics: aggregatedQualitativeMetrics,
    aggregatedMetricsAnswers,
    requiredMaterialMetrics,
    companyLevelReportingUnitId,
    dataLoading,
    optedOutMetrics,
    projectLeader,
    drData,
  } = useGetMetrics(
    withAssociation,
    companyAssessmentId,
    parentAssessmentId,
    standardRef,
    isBusinessUnit,
    noFilter
  );

  const { BUmetrics, requiredBUMaterialMetrics, buDataLoading } = useGetBuMetrics(
    disclosureRequirementRef,
    companyAssessmentId,
    parentAssessmentId,
    isBusinessUnit
  );

  const { data: mdrmTasksData } = useGetMdrmDatapointsTasksQuery({
    variables: { assessmentId: esrsAssessmentId },
    skip: !esrsAssessmentId,
  });

  const nestedBuMetrics = useMemo(
    () => processBuMetrics(BUmetrics as AssessableMetrics, companyAssessmentId, parentAssessmentId),
    [BUmetrics, companyAssessmentId, parentAssessmentId]
  );

  const allFilteredMetrics = useMemo(
    () => filterDuplicateRows(aggregatedMetrics, nestedBuMetrics, isBusinessUnit),
    [aggregatedMetrics, nestedBuMetrics, isBusinessUnit]
  );

  const allNarrativeMetrics = useMemo(
    () => allFilteredMetrics.filter((met) => met.metric.metricType !== QuestionType_Enum_.Decimal_),
    [allFilteredMetrics]
  );

  const narrativeMetrics = useMemo(
    () =>
      allNarrativeMetrics.map((m) =>
        processNarrativeMetrics({
          metric: m,
          answers: isBusinessUnit
            ? companyAnswersData
                .flatMap((ans) => ans.answers)
                .map((ans) => ({
                  metricRef: ans.metricRef ?? '',
                  datapoints: ans.datapoints ?? [],
                }))
            : aggregatedMetricsAnswers?.map((ans) => ({
                metricRef: ans.answer?.metricRef ?? '',
                datapoints: ans.answer?.datapoints ?? [],
              })),
          parentStandardId: parentAssessmentId,
          requiredMaterialMetrics: requiredMaterialMetrics,
        })
      ),
    [
      allNarrativeMetrics,
      isBusinessUnit,
      companyAnswersData,
      aggregatedMetricsAnswers,
      parentAssessmentId,
      requiredMaterialMetrics,
    ]
  );

  const quantitativeMetrics = useMemo(
    () => allFilteredMetrics.filter((m) => m.metric.metricType === QuestionType_Enum_.Decimal_),
    [allFilteredMetrics]
  );

  const taskReferences = getAllQuartersTaskReferences(
    [...quantitativeMetrics, ...narrativeMetrics],
    esrsAssessmentId,
    reportingUnitId ?? companyLevelReportingUnitId
  );

  const { data: taskData, loading: tasksLoading } = useGetDrDatapointsTasksQuery({
    variables: { taskReferences },
    skip: !taskReferences?.length,
  });

  const hasOptedOutChildren = (metric: MetricsTableData['metric']): boolean => {
    if (metric.childrenMetrics) {
      return (
        optedOutMetrics?.includes(metric.reference) ||
        metric.childrenMetrics.some((m) => m.childMetric && hasOptedOutChildren(m.childMetric))
      );
    }
    return optedOutMetrics?.includes(metric.reference) ?? false;
  };

  const unfilteredMetrics = [...quantitativeMetrics, ...narrativeMetrics];

  const additionalInfoFilterOptions = useMemo(() => {
    return uniqBy(
      unfilteredMetrics?.flatMap((met) => {
        return met.metric.additionalTypes.map((additionalType) => ({
          label: additionalType.additionalType?.title ?? '',
          ref: additionalType.additionalType?.reference,
        }));
      }),
      'ref'
    );
  }, [unfilteredMetrics]);

  const materialityFilterOptions = [
    {
      ref: 'material',
      label: framework === FrameworkRefs.vsme ? 'Collect and report' : 'Material',
      disabled: unfilteredMetrics.every((m) => m.metric.materialMetrics[0].isDataGatheringOnly),
      checkFunction: (m: MetricsTableData) =>
        !m.metric.materialMetrics?.[0]?.isDataGatheringOnly &&
        !m.metric.materialMetrics?.[0]?.isPhasedIn,
    },
    {
      ref: 'gatherData',
      label: framework === FrameworkRefs.vsme ? 'Only collect' : 'Not material but collect data',
      disabled: unfilteredMetrics.every((m) => !m.metric.materialMetrics[0].isDataGatheringOnly),
      checkFunction: (m: MetricsTableData) =>
        m.metric.materialMetrics?.[0]?.isDataGatheringOnly &&
        !m.metric.materialMetrics?.[0]?.isPhasedIn,
    },
    {
      ref: 'phasedIn',
      label: 'Material but phased-in',
      disabled: unfilteredMetrics.every((m) => !m.metric.materialMetrics?.[0]?.isPhasedIn),
      checkFunction: (m: MetricsTableData) => m.metric.materialMetrics?.[0]?.isPhasedIn,
    },
  ];

  const orderedMetrics = useMemo(() => {
    let mixedMetrics = [...quantitativeMetrics, ...narrativeMetrics];
    if (
      [
        TaskFilterOptions.members,
        TaskFilterOptions.yourTasks,
        TaskFilterOptions.unassigned,
      ].includes(taskFilter)
    ) {
      mixedMetrics = mixedMetrics.filter((m) => {
        const metricTaskReferences = getAllQuartersTaskReferences(
          [m],
          esrsAssessmentId,
          reportingUnitId ?? companyLevelReportingUnitId
        );

        if (taskFilter === TaskFilterOptions.unassigned) {
          return taskData?.Task.every(
            (task) => !metricTaskReferences.includes(task.taskReference ?? '')
          );
        }

        return (
          taskData?.Task.filter((task) =>
            metricTaskReferences.includes(task.taskReference ?? '')
          )?.some((task) =>
            taskFilter === TaskFilterOptions.members
              ? task.owner.id === selectedUserId
              : task.owner.id === user?.id
          ) ||
          mdrmTasksData?.Task.some(
            (task) =>
              task.datapointMdrm?.datapoint.answer.metricRef === m.metric.reference &&
              (taskFilter === TaskFilterOptions.members
                ? task.ownerId === selectedUserId
                : task.ownerId === user?.id)
          )
        );
      });
    }
    if (groupOrOrder !== GroupOrderOptions.byFrameworkOrder) {
      return mixedMetrics
        .filter((m) => m.metric.metricType === QuestionType_Enum_.Decimal_)
        .filter((m) =>
          selectedQuarter === TimePeriodsEnums.year ? true : hasQuarterlyFrequency(m.metric)
        );
    } else {
      return uniqBy(
        orderBy(
          mixedMetrics,
          [
            (metric) => metric.metric.globalOrder === null,
            (metric) => metric.metric.globalOrder,
            (metric) => metric.metric.isAR,
            (metric) => metric.metric.order,
            (metric) => metric.metric.firstLevel,
            (metric) => metric.metric.secondLevel,
            (metric) => metric.metric.reference,
          ],
          ['asc', 'asc', 'asc', 'asc', 'asc', 'asc', 'asc']
        ).filter((met) =>
          selectedQuarter === TimePeriodsEnums.year
            ? true
            : met.metric.materialMetrics?.[0]?.frequency === FrequencyEnums.quarterly
        ),
        'metric.reference'
      );
    }
  }, [groupOrOrder, quantitativeMetrics, narrativeMetrics, selectedQuarter, taskFilter, taskData]);

  const filteredMetrics = useMemo(() => {
    return orderedMetrics
      .filter((m) =>
        !!selectedAdditionalTypes.length
          ? m.metric.additionalTypes.some((aT) =>
              selectedAdditionalTypes.some((selectedType) =>
                aT.additionalType.reference.toLowerCase().includes(selectedType.toLowerCase())
              )
            )
          : true
      )
      .filter((m) =>
        taskFilter === TaskFilterOptions.markedAsIrrelevant ? hasOptedOutChildren(m.metric) : true
      )
      .filter((m) =>
        selectedMaterialityOptions.length
          ? materialityFilterOptions
              .filter((opt) => selectedMaterialityOptions.includes(opt.ref))
              .some((opt) => opt.checkFunction(m))
          : true
      );
  }, [orderedMetrics, selectedAdditionalTypes, taskFilter]);

  const filteredNarrativeMetrics = useMemo(() => {
    const metricsFilteredByTasks = [
      TaskFilterOptions.members,
      TaskFilterOptions.yourTasks,
      TaskFilterOptions.unassigned,
    ].includes(taskFilter)
      ? narrativeMetrics.filter((m) => {
          const metricTaskReferences = getTaskReferences(
            m,
            selectedQuarter,
            esrsAssessmentId,
            reportingUnitId ?? companyLevelReportingUnitId
          );
          if (taskFilter === TaskFilterOptions.unassigned) {
            return taskData?.Task.every(
              (task) => !metricTaskReferences.includes(task.taskReference ?? '')
            );
          }
          return taskData?.Task.filter((task) =>
            metricTaskReferences.includes(task.taskReference ?? '')
          )?.some((task) =>
            taskFilter === TaskFilterOptions.members
              ? task.owner.id === selectedUserId
              : task.owner.id === user?.id
          );
        })
      : narrativeMetrics;
    const filtered = metricsFilteredByTasks
      .filter((m) =>
        !!selectedAdditionalTypes.length
          ? m.metric.additionalTypes.some((aT) =>
              selectedAdditionalTypes.some((selectedType) =>
                aT.additionalType.reference.toLowerCase().includes(selectedType.toLowerCase())
              )
            )
          : true
      )
      .filter((m) =>
        taskFilter === TaskFilterOptions.markedAsIrrelevant ? hasOptedOutChildren(m.metric) : true
      )
      .filter((m) =>
        selectedMaterialityOptions.length
          ? materialityFilterOptions
              .filter((opt) => selectedMaterialityOptions.includes(opt.ref))
              .some((opt) => opt.checkFunction(m))
          : true
      );
    return filtered;
  }, [taskFilter, narrativeMetrics, taskData, selectedAdditionalTypes]);

  const summaryQualitativeMetrics = useMemo(
    () =>
      isGroup
        ? (aggregatedQualitativeMetrics.filter((metric) => hasChildOnSubLevel(metric)) ?? [])
        : (aggregatedQualitativeMetrics.filter((metric) => hasChildOnBULevel(metric)) ?? []),
    [isGroup, aggregatedQualitativeMetrics]
  );

  const flattenedNarrativeMetrics = useMemo(
    () => flattenMetricsWithTags(narrativeMetrics),
    [narrativeMetrics]
  );

  const [narrativeMetricsMissingTagsConfiguration, someNarrativeTagsNotConfigured] = useMemo(
    () =>
      metricsMissingRequiredTags(
        flattenedNarrativeMetrics,
        companyAssessmentId,
        isGroup,
        isBusinessUnit
      ),
    [flattenedNarrativeMetrics, companyAssessmentId, isGroup, isBusinessUnit]
  );

  const flattenAggregatedMetrics = useMemo(
    () => flattenMetricsNotAggregatable(aggregatedMetrics),
    [aggregatedMetrics]
  );

  const filteredNotAggregatableMetrics = useMemo(
    () =>
      flattenAggregatedMetrics?.filter((mm) => {
        const dataCollectionMatchesLevel = isDataCollectionMatching(
          mm.metric,
          isGroup ? DataCollectionLevel.group : DataCollectionLevel.company
        );
        return (
          mm.metric?.notAggregatable === true && !isBusinessUnit && !dataCollectionMatchesLevel
        );
      }),
    [flattenAggregatedMetrics, isBusinessUnit, isGroup]
  );

  const notAggregatableMetrics = useMemo(
    () => filteredNotAggregatableMetrics?.map((metric) => metric.metric?.reference),
    [filteredNotAggregatableMetrics]
  );

  const parentNotAggregatableMetrics: Set<string> = useMemo(
    () =>
      new Set(
        filteredNotAggregatableMetrics
          ?.map((metric) => metric.metric?.parentMetrics.map((pm) => pm.parentMetricRef))
          .flat()
      ),
    [filteredNotAggregatableMetrics]
  );

  const flattenOrderedMetrics = useMemo(
    () => flattenMetricsWithTags(filteredMetrics),
    [filteredMetrics]
  );

  const [metricsMissingTagsConfiguration, someQuantitativeTagsNotConfigured] = useMemo(
    () =>
      metricsMissingRequiredTags(
        flattenOrderedMetrics,
        companyAssessmentId,
        isGroup,
        isBusinessUnit
      ),
    [flattenOrderedMetrics, companyAssessmentId, isGroup, isBusinessUnit]
  );

  return {
    allMetrics: filteredMetrics,
    quantitativeMetrics: quantitativeMetrics,
    narrativeMetrics: filteredNarrativeMetrics,
    unfilteredMetrics: unfilteredMetrics,
    taskData,
    tasksLoading,
    summaryQualitativeMetrics,
    companyLevelReportingUnitId,
    materialityFilterOptions,
    additionalInfoFilterOptions,
    projectLeader,
    requiredMaterialMetrics: !isBusinessUnit ? requiredMaterialMetrics : requiredBUMaterialMetrics,
    dataLoading: !isBusinessUnit ? dataLoading : buDataLoading,
    optedOutMetrics: optedOutMetrics,
    notAggregatableMetrics,
    parentNotAggregatableMetrics,
    metricsMissingTagsConfiguration,
    someTagsNotConfigured: someQuantitativeTagsNotConfigured || someNarrativeTagsNotConfigured,
    narrativeMetricsMissingTagsConfiguration,
    aggregatedMetricsAnswers,
    drData,
  };
};

const checkIsCollectedOnGroup = (
  metric: any,
  parentStandardId: any,
  isGroup: any,
  requiredMaterialMetrics: any[],
  isMetricMaterialForCompany: any
) => {
  return (
    !isFullMetricOnBULevel(metric) &&
    !!parentStandardId &&
    !isGroup &&
    !areChildrenMaterialOrCollected(metric, parentStandardId, requiredMaterialMetrics) &&
    !isMetricMaterialForCompany
  );
};

const checkIsCollectedOnCompany = (
  metric: any,
  parentStandardId: any,
  isBusinessUnit: any,
  requiredMaterialMetrics: any[],
  isMetricMaterialForCompany: any
) => {
  return (
    !isFullMetricOnBULevel(metric) &&
    isBusinessUnit &&
    (isFullParentMetricOnSubLevel(metric, parentStandardId ?? '', requiredMaterialMetrics) ||
      isMetricMaterialForCompany)
  );
};

const hasRequiredChildMetric = (
  metric: any,
  requiredMaterialMetrics: GetRequiredMaterialMetricsQuery_['esrs_MaterialMetric'],
  isGroup: boolean,
  parentStandardId: string,
  isMetricMaterialForCompany: boolean | null | undefined,
  isBusinessUnit: boolean
): boolean => {
  const checkIsCollected = !isBusinessUnit
    ? (metric: any) =>
        checkIsCollectedOnGroup(
          metric,
          parentStandardId,
          isGroup,
          requiredMaterialMetrics,
          isMetricMaterialForCompany
        )
    : (metric: any) =>
        checkIsCollectedOnCompany(
          metric,
          parentStandardId,
          isBusinessUnit,
          requiredMaterialMetrics,
          isMetricMaterialForCompany
        );
  if (metric.childrenMetrics && metric.childrenMetrics.length === 0) {
    // Leaf node: check if present in requiredMaterialMetrics
    return (
      requiredMaterialMetrics.some((rm) => rm.metric.reference === metric.reference) &&
      !checkIsCollected(metric)
    );
  }
  // Non-leaf: check if any child is required
  return metric.childrenMetrics.some(
    (child: any) =>
      child.childMetric &&
      hasRequiredChildMetric(
        child.childMetric,
        requiredMaterialMetrics,
        isGroup,
        parentStandardId,
        isMetricMaterialForCompany,
        isBusinessUnit
      )
  );
};

const isRequired = (
  metric: AggregatedMetricsTableData,
  requiredMaterialMetrics: GetRequiredMaterialMetricsQuery_['esrs_MaterialMetric']
): boolean => {
  const isMetricRequired = requiredMaterialMetrics.some(
    (m) => m.metric.reference === metric.metric.reference
  );
  if (!!metric.subRows?.length) {
    return metric.subRows?.some((subRow) => isRequired(subRow, requiredMaterialMetrics));
  }
  return isMetricRequired;
};

export const filterCollectedChildMetrics = (
  metricObj: AggregatedMetricsTableData,
  parentStandardId: string,
  isGroup: boolean,
  requiredMaterialMetrics: GetRequiredMaterialMetricsQuery_['esrs_MaterialMetric'],
  isMetricMaterialForCompany: boolean | null | undefined,
  isBusinessUnit: boolean
): AggregatedMetricsTableData | null => {
  // If there are no subRows, just check if this metric (using childrenMetrics) is required
  if (
    metricObj.subRows &&
    metricObj.metric.metricType === QuestionType_Enum_.YesNo_ &&
    metricObj.subRows.length === 1
  ) {
    if (isRequired(metricObj, requiredMaterialMetrics)) return metricObj;
    if (
      hasRequiredChildMetric(
        metricObj.metric,
        requiredMaterialMetrics,
        isGroup,
        parentStandardId,
        isMetricMaterialForCompany,
        isBusinessUnit
      )
    ) {
      return metricObj;
    }
    return null;
  }

  // check if metric is material
  if (
    metricObj.metric &&
    !isRequired(metricObj, requiredMaterialMetrics) &&
    metricObj.metric.metricType !== QuestionType_Enum_.Decimal_
  ) {
    return null;
  }

  if (!metricObj.subRows || metricObj.subRows.length === 0) {
    return metricObj;
  }

  // Otherwise, filter subRows recursively
  const filteredSubrows =
    metricObj.subRows
      ?.map((row) =>
        filterCollectedChildMetrics(
          row,
          parentStandardId,
          isGroup,
          requiredMaterialMetrics,
          isMetricMaterialForCompany,
          isBusinessUnit
        )
      )
      ?.filter((r): r is AggregatedMetricsTableData => !!r) ?? [];

  // If all subRows are filtered out, remove this parent as well
  if (
    (filteredSubrows.length === 1 &&
      filteredSubrows[0]?.metric?.reference === metricObj.metric?.reference) ||
    filteredSubrows.length == 0
  ) {
    return null;
  }

  return {
    ...metricObj,
    subRows: filteredSubrows,
    // childrenMetrics is left untouched
  };
};
